/**
 * Demo-Censor Helpers
 *
 * Zensur-Funktionen für den Live-Demo Embed-Mode der KI-Zentrale.
 * Jede Funktion nimmt den Originalwert und ein `isEmbed`-Flag. Wenn
 * `isEmbed === false` wird der Originalwert unverändert zurückgegeben —
 * im normalen CRM gibt es also 0 % Verhaltensänderung.
 *
 * Die Funktionen sind bewusst robust gegen `null`/`undefined`/nicht-
 * String-Werte, damit sie ohne zusätzliche Null-Checks in den JSX-
 * Rendern eingesetzt werden können.
 */

const BULLET = '\u2022'; // •

function repeat(ch, n) {
  return new Array(Math.max(0, n) + 1).join(ch);
}

/**
 * Zensiert einen Personennamen: erster Buchstabe jedes Wortteils bleibt,
 * der Rest wird durch Bullets ersetzt. Mindestens 3 Bullets pro Wort,
 * damit auch sehr kurze Vornamen maskiert aussehen.
 *
 *  "Max Mustermann"   → "M••• M••••••••"
 *  "Dr. Hans Müller"  → "D•• H••• M•••••"
 *  "ACME GmbH"        → "A••• G•••"
 */
export function censorName(name, isEmbed) {
  if (!isEmbed) return name;
  if (name == null) return name;
  const s = String(name).trim();
  if (!s) return s;
  return s
    .split(/\s+/)
    .map(word => {
      if (!word) return word;
      const first = word.charAt(0);
      const rest = Math.max(word.length - 1, 3);
      return first + repeat(BULLET, rest);
    })
    .join(' ');
}

/**
 * Zensiert eine E-Mail-Adresse: erster Buchstabe des local-parts bleibt,
 * Rest des local-parts + komplette Domain werden durch Bullets ersetzt.
 * Top-Level-Domain bleibt als Platzhalter `.••`.
 *
 *  "max@werkpilot.de"  → "m•••@•••••••.••"
 *  "kontakt@acme.com"  → "k••••@••••.••"
 *  "noreply"           → "n••••••"  (kein @ vorhanden)
 */
export function censorEmail(email, isEmbed) {
  if (!isEmbed) return email;
  if (email == null) return email;
  const s = String(email).trim();
  if (!s) return s;
  const at = s.indexOf('@');
  if (at < 0) {
    // Kein @ — wie ein Name behandeln
    return s.charAt(0) + repeat(BULLET, Math.max(s.length - 1, 5));
  }
  const local = s.slice(0, at);
  const domain = s.slice(at + 1);
  const localCensored = (local.charAt(0) || '') + repeat(BULLET, Math.max(local.length - 1, 3));
  // Domain: letzter Teil (tld) als ".••" darstellen, Rest als Bullets
  const dot = domain.lastIndexOf('.');
  const domainCensored = dot > 0
    ? repeat(BULLET, Math.max(dot, 4)) + '.' + repeat(BULLET, 2)
    : repeat(BULLET, Math.max(domain.length, 4));
  return `${localCensored}@${domainCensored}`;
}

/**
 * Zensiert einen Betreff / Subject — ersetzt komplett durch einen
 * Platzhalter, weil Subjects oft Namen und Auftragsdetails enthalten.
 */
export function censorSubject(subject, isEmbed) {
  if (!isEmbed) return subject;
  if (subject == null || subject === '') return subject;
  return 'Betreff (Demo zensiert)';
}

/**
 * Zensiert einen Nachrichten-Body / Email-Inhalt / Call-Transcript.
 * Ersetzt komplett durch einen Platzhalter.
 */
export function censorBody(body, isEmbed) {
  if (!isEmbed) return body;
  if (body == null || body === '') return body;
  return 'Nachrichteninhalt (für Demo zensiert)';
}

/**
 * Zensiert eine Telefonnummer zu "•••• ••• ••••". Äquivalent zur
 * bereits existierenden Inline-Variante, hier als Helper für Konsistenz.
 */
export function censorPhone(phone, isEmbed) {
  if (!isEmbed) return phone;
  if (phone == null || phone === '') return phone;
  return '•••• ••• ••••';
}

/**
 * Smart-Censor für Contact-Felder, die je nach Kanal ein Name, eine
 * E-Mail-Adresse oder eine Telefonnummer sein können. Erkennt das Format
 * automatisch und wählt die passende Zensur-Funktion.
 */
export function censorContact(value, isEmbed) {
  if (!isEmbed) return value;
  if (value == null || value === '') return value;
  const s = String(value).trim();
  if (s.includes('@')) return censorEmail(s, isEmbed);
  // Phone: beginnt mit + oder besteht (nach Entfernung von Leerzeichen,
  // Bindestrichen und Klammern) ausschließlich aus Ziffern
  const digitsOnly = s.replace(/[\s\-()/]/g, '');
  if (/^\+?\d{5,}$/.test(digitsOnly)) return censorPhone(s, isEmbed);
  return censorName(s, isEmbed);
}
